import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { SALayout } from "../../components/superadmin/SALayout";
import { formatDate } from "../../lib/format";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Building2, CheckCircle2, Beaker, Users, Beer, Repeat, IndianRupee, MessageCircle, AlertTriangle, Clock } from "lucide-react";

const CARDS = [
  { key: "total_venues", label: "Total Venues", icon: Building2 },
  { key: "active_venues", label: "Active Venues", icon: CheckCircle2 },
  { key: "trial_venues", label: "Trial Venues", icon: Beaker },
  { key: "total_customers", label: "Total Customers", icon: Users },
  { key: "active_tabs", label: "Active Bottle Tabs", icon: Beer },
  { key: "repeat_rate", label: "Repeat Visit Rate %", icon: Repeat, suffix: "%" },
  { key: "mrr", label: "MRR", icon: IndianRupee, prefix: "₹" },
  { key: "wa_this_month", label: "WhatsApp This Month", icon: MessageCircle },
];

export default function SADashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/dashboard/charts").then(({ data }) => setCharts(data));
    api.get("/admin/dashboard/alerts").then(({ data }) => setAlerts(data));
  }, []);

  return (
    <SALayout>
      <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-6">Dashboard</h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {CARDS.map((c, i) => {
          const Icon = c.icon;
          const val = stats?.[c.key];
          const display = val == null ? "—"
            : `${c.prefix || ""}${Number(val).toLocaleString("en-IN")}${c.suffix || ""}`;
          return (
            <div
              key={c.key} data-testid={`sa-card-${c.key}`}
              style={{ animationDelay: `${i * 30}ms` }}
              className="tb-fade-up bg-[#1A1A1A] border border-white/10 rounded-xl p-5 hover:border-[#9D00FF]/40 transition"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider text-[#A0A0A0]">{c.label}</div>
                <Icon size={16} className="text-[#9D00FF]" />
              </div>
              <div className="text-3xl font-black text-white">{display}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <ChartCard title="New Venues Onboarded — last 12 months">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={charts?.months || []}>
              <CartesianGrid stroke="#ffffff10" />
              <XAxis dataKey="month" stroke="#A0A0A0" fontSize={11} />
              <YAxis stroke="#A0A0A0" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #9D00FF33" }} />
              <Line type="monotone" dataKey="new_venues" stroke="#9D00FF" strokeWidth={2} dot={{ r: 3, fill: "#9D00FF" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Top 10 Venues — Active Bottle Tabs">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts?.top_venues || []} layout="vertical">
              <CartesianGrid stroke="#ffffff10" />
              <XAxis type="number" stroke="#A0A0A0" fontSize={11} />
              <YAxis type="category" dataKey="name" stroke="#A0A0A0" fontSize={11} width={110} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #9D00FF33" }} />
              <Bar dataKey="tabs" fill="#9D00FF" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Platform Repeat Visit Rate % — last 12 months" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={charts?.months || []}>
              <CartesianGrid stroke="#ffffff10" />
              <XAxis dataKey="month" stroke="#A0A0A0" fontSize={11} />
              <YAxis stroke="#A0A0A0" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #9D00FF33" }} />
              <Line type="monotone" dataKey="repeat_rate" stroke="#9D00FF" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <AlertList
          title="Needs Your Attention"
          icon={AlertTriangle}
          testid="sa-needs-attention"
          rows={alerts?.needs_attention || []}
          render={(v) => ({ primary: v.name, secondary: `${v.city || "—"} · ${v.health_label}`, meta: v.last_activity_at ? `Last activity ${formatDate(v.last_activity_at)}` : "No activity" })}
        />
        <AlertList
          title="Trials Expiring Soon"
          icon={Clock}
          testid="sa-trials-expiring"
          rows={alerts?.trials_expiring || []}
          render={(v) => ({ primary: v.name, secondary: `Expires ${formatDate(v.trial_expiry)}`, meta: "" })}
        />
      </div>
    </SALayout>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`bg-[#1A1A1A] border border-white/10 rounded-xl p-5 ${className}`}>
      <div className="text-sm font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function AlertList({ title, icon: Icon, rows, render, testid }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-[#9D00FF]" />
        <h3 className="font-semibold">{title}</h3>
        <span className="text-xs text-[#A0A0A0]">({rows.length})</span>
      </div>
      {rows.length === 0 && <p className="text-sm text-[#A0A0A0]">Nothing here. Nice.</p>}
      <div className="flex flex-col divide-y divide-white/5">
        {rows.map((r) => {
          const v = render(r);
          return (
            <div key={r.id} className="flex items-center justify-between py-2.5">
              <div className="min-w-0">
                <div className="font-semibold text-white truncate">{v.primary}</div>
                <div className="text-xs text-[#A0A0A0]">{v.secondary}</div>
                {v.meta && <div className="text-[10px] text-[#A0A0A0]">{v.meta}</div>}
              </div>
              <Link to={`/admin/venues/${r.id}`} className="text-xs text-[#9D00FF] font-semibold hover:underline">View →</Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}
